import { useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading, Typography } from "@/components/atoms";
import { GeneralFooter, GeneralHeader, JobRatingPanel } from "@/components/organisms";
import { useSiteConfig } from "@/hooks";
import { useLazyVerifyProviderQuery } from "@/redux/apis/auth/authApi";
import { useLazyGetJobDetailsQuery } from "@/redux/apis/jobRating/jobRatingApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveOperator } from "@/redux/slices/operator/operatorSlice";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { JobRatingExpired } from "./JobRatingExpired";
import { JobRatingSuccess } from "./JobRatingSuccess";

export const JobRating = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { apiUrl } = useSiteConfig();
  const { jobId, actionKey } = useParams();
  const [getJobDetails, { isError, error, isFetching, data: job }] = useLazyGetJobDetailsQuery();
  // Only a 404 (unknown job or wrong link key) means the link is no longer usable; anything else is a load failure.
  const isLinkInvalid = !!error && "status" in error && error.status === 404;
  const [verifyProvider] = useLazyVerifyProviderQuery();
  const operator = useAppSelector(activeOperatorSelector);

  useEffect(() => {
    if (jobId && actionKey) getJobDetails({ id: jobId, actionKey });
  }, [jobId, actionKey, getJobDetails]);

  useEffect(() => {
    if (isLinkInvalid) navigate("expired");
  }, [isLinkInvalid, navigate]);

  useEffect(() => {
    if (job) {
      if (job.rating.metadata.is_rated) navigate("success");
      else {
        if (job.provider.provider_key !== operator?.providerCode) {
          verifyProvider({ providerCode: job.provider.provider_key, apiUrl })
            .unwrap()
            .then((operator) => dispatch(setActiveOperator(operator)));
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job]);

  return (
    <div className="flex min-h-screen flex-col overflow-hidden md:flex-row">
      <div className="relative flex flex-1 flex-col">
        <GeneralHeader />
        <div className="relative flex-1 flex-col">
          {isFetching && <Loading />}
          {isError && !isLinkInvalid && !isFetching && (
            <div className="px-5 pt-[60px] text-center">
              <Typography variant="paragraph">We couldn&apos;t load your booking right now. Please try again later.</Typography>
            </div>
          )}
          {job && <JobRatingPanel job={job} />}
        </div>
        <GeneralFooter />
      </div>
    </div>
  );
};

JobRating.Success = JobRatingSuccess;
JobRating.Expired = JobRatingExpired;
